import { LABEL_API } from '~/lib/constants';

export type LabelTranslationKey =
  | 'A4_STANDARD'
  | 'A4_STANDARD_ECO'
  | 'A4_STANDARD_PARCELS_ON_SEPARATE_PAGES'
  | 'A4_STICKERS_TWO_PER_PAGE'
  | 'A4_STICKERS_FOUR_PER_PAGE'
  | 'FOUR_BY_SIX'
  | 'UNKNOWN';

export const labelTranslationMap: { [key in LabelTranslationKey]: string } = {
  A4_STANDARD: 'A4 Standard\nPrinter',
  A4_STANDARD_ECO: 'A4 Standard\n(Multiple Labels per Page)',
  A4_STANDARD_PARCELS_ON_SEPARATE_PAGES: 'A4 Printer\nParcels on Separate Pages',
  A4_STICKERS_TWO_PER_PAGE: 'A4 Stickers\nTwo per Page',
  A4_STICKERS_FOUR_PER_PAGE: 'A4 Stickers\nFour per Page',
  FOUR_BY_SIX: '4x6 Label\nPrinter',
  UNKNOWN: 'Unknown Label Format'
};

type LabelOption =
  | {
      pageSize: 'A4';
      paperType: 'Standard';
      layout: null;
    }
  // returned when more than one parcel
  | {
      pageSize: 'A4';
      paperType: 'Standard';
      layout: 'Eco';
    }
  // returned when more than one parcel
  | {
      pageSize: 'A4';
      paperType: 'Standard';
      layout: 'ParcelsOnSeparatePages';
    }
  // returned when all non-A4 labels fit on half page
  | {
      pageSize: 'A4';
      paperType: 'Stickers';
      layout: 'TwoPerPage';
    }
  // returned when all non-A4 labels fit on quarter page
  | {
      pageSize: 'A4';
      paperType: 'Stickers';
      layout: 'FourPerPage';
    }
  // returned when _any_ labels fit on 4x6
  | {
      pageSize: 'FourBySix';
      paperType: null;
      layout: null;
    };

export type StatusData = {
  result: {
    orderLineStatuses: { orderLineId: number; status: string }[];
    labelOptions: LabelOption[];
  };
  success: boolean;
  error: object;
};

const upperCaseAndSplitByCapitalLetters = (s: string) => {
  return s
    .replace(/([A-Z])/g, ' $1')
    .toUpperCase()
    .trim()
    .replace(/ /g, '_');
};

export const generateTranslationKey = (type: LabelOption) => {
  let output = type.pageSize;
  if (type.paperType) {
    output += type.paperType;
  }
  if (type.layout) {
    output += type.layout;
  }
  return upperCaseAndSplitByCapitalLetters(output);
};

export const isReady = (d: StatusData) =>
  d?.success && d?.result?.orderLineStatuses?.every((x) => x.status === 'Ready');

export const generateHref = (
  option: LabelOption,
  tenant: string,
  labelWithPackingSlip: boolean,
  orderId?: string,
  orderLineId?: string
) => {
  let base = orderId
    ? `/${tenant}/labels/authenticated/order?orderId=${orderId}`
    : `/${tenant}/labels/authenticated/orderline?orderLineId=${orderLineId}`;

  if (option.pageSize) {
    base += `&pageSize=${option.pageSize}`;
  }

  if (option.paperType) {
    base += `&paperType=${option.paperType}`;
  }

  if (option.layout) {
    base += `&layout=${option.layout}`;
  }

  if (labelWithPackingSlip) {
    base += `&labelDetail=1`;
  }

  return base;
};

export const getSanitisedEndpoint = (statusEndpoint: string) => {
  return statusEndpoint.includes('/label/status') && !statusEndpoint?.includes('authenticated')
    ? `${LABEL_API}${statusEndpoint.replace('/label/status?', '/labels/authenticated/status?').split('&hash=')[0]}`
    : `${LABEL_API}${statusEndpoint}`;
};
